import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  InputBase,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import ReactCountryFlag from 'react-country-flag';
import { supportedLanguages, type LanguageCode } from '../../i18n';
import { UserSettingsManager } from '../../Utils/UserSettingsManager';

interface LanguageSelectorProps {
  variant?: 'icon' | 'full';
  userSettingsManager?: UserSettingsManager;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'icon', userSettingsManager }) => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Use i18n.language for reactive updates
  const currentLang = (i18n.language?.split('-')[0] as LanguageCode) || 'en';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleLanguageChange = async (langCode: LanguageCode) => {
    if (langCode === currentLang) {
      handleClose();
      return;
    }

    // Always change language locally first (ensures immediate UI update)
    const { changeLanguage } = await import('../../i18n');
    try {
      await changeLanguage(langCode);
    } catch (error) {
      console.error('Failed to change language:', error);
    }

    // If logged in, also save to server
    if (userSettingsManager) {
      userSettingsManager.updateSetting('language', langCode, { immediate: true });
    }

    handleClose();
  };

  const currentLanguage = supportedLanguages.find(lang => lang.code === currentLang);

  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return supportedLanguages;
    const query = searchQuery.toLowerCase();
    return supportedLanguages.filter(
      lang =>
        lang.name.toLowerCase().includes(query) ||
        lang.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <>
      {variant === 'icon' ? (
        <IconButton
          onClick={handleClick}
          color="inherit"
          size="small"
          aria-label={t('language')}
          sx={{
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '6px',
            padding: '6px',
          }}
        >
          {currentLanguage ? (
            <ReactCountryFlag
              countryCode={currentLanguage.flag}
              svg
              style={{ width: '20px', height: '15px' }}
            />
          ) : (
            <LanguageIcon />
          )}
        </IconButton>
      ) : (
        <Box onClick={handleClick} sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <ListItemIcon>
            {currentLanguage && (
              <ReactCountryFlag
                countryCode={currentLanguage.flag}
                svg
                style={{ width: '21px' }}
              />
            )}
          </ListItemIcon>
          <ListItemText>{currentLanguage?.name || 'Language'}</ListItemText>
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 220,
            mt: 1,
            backgroundColor: '#1a1b20',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            overflow: 'hidden',
          },
        }}
        MenuListProps={{
          sx: { padding: 0 },
        }}
      >
        {/* Search Input */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#1a1b20',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            padding: '8px 12px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'rgba(255,255,255,0.06)',
              borderRadius: '6px',
              padding: '6px 10px',
              border: '1px solid rgba(255,255,255,0.08)',
              '&:focus-within': {
                border: '1px solid rgba(25, 229, 224, 0.5)',
                backgroundColor: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />
            <InputBase
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              sx={{
                flex: 1,
                color: 'white',
                fontSize: '13px',
                '& input': {
                  padding: 0,
                  '&::placeholder': {
                    color: 'rgba(255,255,255,0.4)',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Language List */}
        <Box
          sx={{
            maxHeight: '240px',
            overflowY: 'auto',
            overflowX: 'hidden',
            /* Custom scrollbar */
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(128, 128, 128, 0.5)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(169, 169, 169, 0.8)',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(128, 128, 128, 0.5) transparent',
          }}
        >
          {filteredLanguages.length === 0 ? (
            <Box
              sx={{
                padding: '16px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '13px',
              }}
            >
              No languages found
            </Box>
          ) : (
            filteredLanguages.map((language) => (
              <MenuItem
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                selected={currentLang === language.code}
                sx={{
                  py: 1.2,
                  px: 1.5,
                  minHeight: 'unset',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 229, 224, 0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 229, 224, 0.2)',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ReactCountryFlag
                    countryCode={language.flag}
                    svg
                    style={{ width: '22px', height: '16px' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={language.name}
                  primaryTypographyProps={{
                    fontSize: '13px',
                    fontWeight: currentLang === language.code ? 500 : 400,
                  }}
                />
                {currentLang === language.code && (
                  <CheckIcon sx={{ fontSize: 16, color: '#19e5e0' }} />
                )}
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>
    </>
  );
};

export default LanguageSelector;
